import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import { Network } from "lucide-react";

// --- Providers e Layouts (Carregamento Imediato) ---
import { NavigationProvider } from "@/lib/navigation-context";
import { AprovacaoNotificationsProvider } from "@/lib/approval-notifications-context";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { withPermissionPage } from "@/components/hocs/WithPermissionPage";
import { withResourceView } from "@/components/hocs/WithResourceView";
import { PermissaoEnum } from "@workflow/types";
import MainLayout from "./components/layout/MainLayout";
import { RouteErrorPage } from "./pages/RouteErrorPage.tsx";
import TarefaDetailPage from "./pages/tarefas/TarefaDetailPage.tsx";
import TarefaAdminListPage from "./pages/tarefas/TarefaAdminListPage.tsx";
import SubMenuDashboard from "./pages/SubMenuDashboard.tsx";
import { ClienteAdminListPage } from "./pages/clientes/ClienteAdminListPage.tsx";
import { SquadAdminListPage } from "./pages/squads/SquadAdminListPage.tsx";
import UserListaPage from "./pages/user-roles/UserListaPage.tsx";
import RolePermissionsPage from "./pages/user-roles/RolePermissionsPage.tsx";
import { ProcessamentoIAAdminPage } from "./pages/processamentos-ia/ProcessamentoIAAdminPage.tsx";
import ArquivosAdminPage from "./pages/admin/arquivos/ArquivosAdminPage.tsx";
import NotificacoesAdminPage from "./pages/admin/notificacoes/NotificacoesAdminPage.tsx";
import ComunicadosAdminPage from "./pages/admin/comunicados/ComunicadosAdminPage.tsx";
import ComunicadoSistemaCreatePage from "./pages/admin/comunicados/ComunicadoSistemaCreatePage.tsx";
import ComunicadoSistemaEditPage from "./pages/admin/comunicados/ComunicadoSistemaEditPage.tsx";
import ComunicadoDetailsPage from "./pages/admin/comunicados/ComunicadoDetailsPage.tsx";
import ComunicadosHistoricoPage from "./pages/comunicados/ComunicadosHistoricoPage.tsx";
import ServicosExternosPage from "./pages/admin/servicos-externos/ServicosExternosPage.tsx";
import NotificacaoTestePage from "./pages/admin/servicos-externos/NotificacaoTestePage.tsx";

// --- Loader Elegante ---
export const PageLoader = () => (
  <div className="flex h-screen w-full flex-col items-center justify-center p-8">
    {/* Animação de entrada substituindo o initial/animate do framer-motion */}
    <div className="flex flex-col items-center gap-6 duration-500 ease-out animate-in fade-in zoom-in-95">
      <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-border/30 bg-card/20 shadow-sm backdrop-blur-md">
        {/* Efeito de opacidade (pulsar) usando a classe nativa do Tailwind */}
        <div className="animate-pulse">
          <Network className="h-6 w-6 text-primary/80" />
        </div>

        {/* A borda giratória já estava excelente em Tailwind, mantemos igual! */}
        <div className="absolute inset-0 animate-[spin_3s_linear_infinite] rounded-2xl border border-primary/20 border-t-primary/60" />
      </div>

      <div className="flex flex-col items-center gap-1.5">
        <h3 className="font-mono text-[13px] font-medium text-foreground">Carregando módulo...</h3>
      </div>
    </div>
  </div>
);

// --- HOC de Lazy Loading ---
const Loadable = (Component: React.LazyExoticComponent<any>) => (props: any) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);

// --- Importação sob demanda (Lazy) das Páginas ---
const Login = Loadable(lazy(() => import("./pages/login/Login")));
const Menu = Loadable(lazy(() => import("./pages/Menu")));
const NotFound = Loadable(lazy(() => import("./pages/NotFound")));

// Jobs
const JobPage = Loadable(lazy(() => import("./pages/job/JobListPage")));
const JobPageCreate = Loadable(lazy(() => import("./pages/job/JobPageCreate")));
const JobPageEdit = Loadable(lazy(() => import("./pages/job/JobPageEdit")));
const JobPageDetail = withResourceView(
  Loadable(lazy(() => import("./pages/job/JobPageDetail"))),
  "job",
  "geral",
);
const JobPlanejamentoListPage = Loadable(
  lazy(() => import("./pages/job-planejamento/JobPlanejamentoListPage")),
);
const JobPlanejamentoPageDetail = withResourceView(
  Loadable(lazy(() => import("./pages/job-planejamento/JobPlanejamentoPageDetail"))),
  "job",
  "planejamento",
);
const JobPlanejamentoAgendaPage = Loadable(
  lazy(() => import("./pages/job-planejamento/agenda/JobPlanejamentoAgendaPage")),
);

const JobTecnologiaListPage = Loadable(
  lazy(() => import("./pages/job-tecnologia/JobTecnologiaListPage.tsx")),
);
const JobTecnologiaPageDetail = withResourceView(
  Loadable(lazy(() => import("./pages/job-tecnologia/JobTecnologiaPageDetail.tsx"))),
  "job",
  "tecnologia",
);
const JobTecnologiaAgendaPage = Loadable(
  lazy(() => import("./pages/job-tecnologia/agenda/JobTecnologiaAgendaPage")),
);

const CriacaoListPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/criacao/CriacaoListPage"))),
  [PermissaoEnum.ROTA_CRIACAO_JOBS],
);
// o gate de ROTA_CRIACAO_JOBS vem do catálogo de visões, não precisa do withPermissionPage aqui
const CriacaoJobPageDetail = withResourceView(
  Loadable(lazy(() => import("./pages/criacao/CriacaoJobPageDetail"))),
  "job",
  "criacao",
);
const PainelClientePage = withPermissionPage(
  Loadable(lazy(() => import("./pages/clientes/PainelClientePage"))),
  [PermissaoEnum.ROTA_ATENDIMENTO_CLIENTES],
);

const PlanilhaProducaoGraficaListPage = Loadable(
  lazy(() => import("./pages/planilha-producao-grafica/PlanilhaProducaoGraficaListPage.tsx")),
);
const PlanilhaProducaoGraficaDetailPage = withResourceView(
  Loadable(
    lazy(() => import("./pages/planilha-producao-grafica/PlanilhaProducaoGraficaDetailPage.tsx")),
  ),
  "planilha",
  "producao-grafica",
);

// Admin — Usuários
const UsuarioCreate = Loadable(lazy(() => import("./pages/user-roles/UsuarioCreate")));

// Atendimentos
const AtendimentoList = Loadable(lazy(() => import("./pages/atendimento/AtendimentoList")));
const AtendimentoCreate = Loadable(lazy(() => import("./pages/atendimento/AtendimentoCreate")));
const AtendimentoEdit = Loadable(lazy(() => import("./pages/atendimento/AtendimentoEdit")));
const AtendimentoPageDetail = withResourceView(
  Loadable(lazy(() => import("./pages/atendimento/AtendimentoPageDetail"))),
  "planilha",
  "atendimento",
);
const AgendaJobsPage = Loadable(lazy(() => import("./pages/atendimento/agenda/AgendaJobsPage")));
const AtendimentoClientesPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/atendimento/AtendimentoClientesPage"))),
  [PermissaoEnum.ROTA_ATENDIMENTO_CLIENTES],
);
const AtendimentoProdutividadePage = withPermissionPage(
  Loadable(lazy(() => import("./pages/produtividade/AtendimentoProdutividadePage"))),
  [PermissaoEnum.ROTA_PRODUTIVIDADE_ATENDIMENTO],
);

// Faturamento Atendimento
const FaturamentoAtendimentoList = Loadable(
  lazy(() =>
    import("./pages/atendimento/faturamento/FaturamentoAtendimentoList").then((m) => ({
      default: m.FaturamentoAtendimentoList,
    })),
  ),
);
const FaturamentoAtendimentoCreate = Loadable(
  lazy(() => import("./pages/atendimento/faturamento/FaturamentoCreate")),
);
const FaturamentoAtendimentoDetails = withResourceView(
  Loadable(
    lazy(() =>
      import("./pages/atendimento/faturamento/FaturamentoAtendimentoDetails").then((m) => ({
        default: m.FaturamentoAtendimentoDetails,
      })),
    ),
  ),
  "faturamento",
  "atendimento",
);

// Creditáveis
const CreditaveisList = Loadable(lazy(() => import("./pages/creditaveis/CreditaveisList")));
const CreditoDetails = Loadable(
  lazy(() => import("./pages/credito/CreditoDetails").then((m) => ({ default: m.CreditoDetails }))),
);

// Produção e Pré-produção
const PreProductionList = Loadable(lazy(() => import("./pages/pre-producao/PreProducaoList")));
const PreProducaoPageDetails = withResourceView(
  Loadable(lazy(() => import("./pages/pre-producao/PreProducaoPageDetails"))),
  "planilha",
  "pre-producao",
);
const PreProducaoVersaoCreate = Loadable(
  lazy(() => import("./pages/pre-producao/PreProducaoVersaoCreate")),
);
const VersoesListPage = Loadable(
  lazy(() =>
    import("./pages/pre-producao/VersoesListPage").then((m) => ({ default: m.VersoesListPage })),
  ),
);
const PreProducaoAgendaPage = Loadable(
  lazy(() => import("./pages/pre-producao/agenda/PreProducaoAgendaPage")),
);

// Financeiro e Compras
const ProducaoFinanceiraList = Loadable(
  lazy(() => import("./pages/producao-financeira/ProducaoFinanceiraList")),
);
const ProducaoFinanceiraCreate = Loadable(
  lazy(() =>
    import("./pages/producao-financeira/ProducaoFinanceiraCreate").then((m) => ({
      default: m.ProducaoFinanceiraCreate,
    })),
  ),
);
const ProducaoFinanceiraDetails = Loadable(
  lazy(() =>
    import("./pages/producao-financeira/ProducaoFinanceiraDetails").then((m) => ({
      default: m.ProducaoFinanceiraDetails,
    })),
  ),
);
const ProducaoFinanceiraEdit = Loadable(
  lazy(() =>
    import("./pages/producao-financeira/ProducaoFinanceiraEdit").then((m) => ({
      default: m.ProducaoFinanceiraEdit,
    })),
  ),
);
const ConciliacaoList = Loadable(
  lazy(() =>
    import("./pages/producao-financeira/conciliacao/ConciliacaoList").then((m) => ({
      default: m.ConciliacaoList,
    })),
  ),
);
const ConciliacaoDetails = Loadable(
  lazy(() =>
    import("./pages/producao-financeira/conciliacao/ConciliacaoDetails").then((m) => ({
      default: m.ConciliacaoDetails,
    })),
  ),
);
const ConciliacaoConciliar = Loadable(
  lazy(() =>
    import("./pages/producao-financeira/conciliacao/ConciliacaoConciliar").then((m) => ({
      default: m.ConciliacaoConciliar,
    })),
  ),
);
const FaturamentoFinanceiroList = Loadable(
  lazy(() =>
    import("./pages/financeiro/FaturamentoFinanceiroList").then((m) => ({
      default: m.FaturamentoFinanceiroList,
    })),
  ),
);
const FaturamentoFinanceiroDetails = withResourceView(
  Loadable(
    lazy(() =>
      import("./pages/financeiro/FaturamentoFinanceiroDetails").then((m) => ({
        default: m.FaturamentoFinanceiroDetails,
      })),
    ),
  ),
  "faturamento",
  "financeiro",
);
const CustoFixoPage = Loadable(lazy(() => import("./pages/financeiro/custo-fixo/CustoFixoPage")));
const CustoFixoContratosPage = Loadable(lazy(() => import("./pages/custo-fixo/ContratosPage")));
const CustoFixoCadastrosPage = Loadable(lazy(() => import("./pages/custo-fixo/CadastrosPage")));
const ContatosPage = Loadable(lazy(() => import("./pages/contato/ContatosPage")));
const ContatoCreatePage = Loadable(lazy(() => import("./pages/contato/ContatoCreatePage")));
const PedirBrindePage = Loadable(lazy(() => import("./pages/contato/PedirBrindePage")));
const PedidoBrindeDetalhePage = Loadable(
  lazy(() => import("./pages/contato/PedidoBrindeDetalhePage")),
);
const ContatoDetalhePage = Loadable(lazy(() => import("./pages/contato/ContatoDetalhePage")));
const EnviosPage = Loadable(lazy(() => import("./pages/facilities/EnviosPage")));
const ContratoCreatePage = Loadable(lazy(() => import("./pages/custo-fixo/ContratoCreatePage")));
const ContratoDetalhePage = Loadable(lazy(() => import("./pages/custo-fixo/ContratoDetalhePage")));
const CentroCustoDetalhePage = Loadable(
  lazy(() => import("./pages/custo-fixo/CentroCustoDetalhePage")),
);
const PlanejamentoPage = Loadable(lazy(() => import("./pages/custo-fixo/PlanejamentoPage")));
const PlanejamentoNovaVersaoPage = Loadable(
  lazy(() => import("./pages/custo-fixo/PlanejamentoNovaVersaoPage")),
);
const PlanejamentoEditarVersaoPage = Loadable(
  lazy(() => import("./pages/custo-fixo/PlanejamentoEditarVersaoPage")),
);
const FechamentoHubPage = Loadable(lazy(() => import("./pages/custo-fixo/FechamentoHubPage")));
const FechamentoConsolidadoPage = Loadable(
  lazy(() => import("./pages/custo-fixo/FechamentoConsolidadoPage")),
);
const ConsolidacaoPage = Loadable(lazy(() => import("./pages/custo-fixo/ConsolidacaoPage")));
const FechamentoPage = Loadable(lazy(() => import("./pages/custo-fixo/FechamentoPage")));
const CompraList = Loadable(lazy(() => import("./pages/compra/CompraList")));
const CompraPageDetail = withResourceView(
  Loadable(lazy(() => import("./pages/compra/CompraPageDetail"))),
  "planilha",
  "compras",
);
const CompraPageCreate = Loadable(lazy(() => import("./pages/compra/CompraPageCreate")));
const CompraPageEdit = Loadable(lazy(() => import("./pages/compra/CompraPageEdit")));

// Outros
const AprovacaoPage = Loadable(lazy(() => import("./pages/aprovacao/AprovacaoPage")));
const AprovacaoConfigListPage = Loadable(
  lazy(() => import("./pages/admin/aprovacoes/AprovacaoConfigListPage")),
);
const AprovacaoConfigEditorPage = Loadable(
  lazy(() => import("./pages/admin/aprovacoes/AprovacaoConfigEditorPage")),
);
const FluxoAprovacaoHistoricoPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/admin/aprovacoes/historico/FluxoAprovacaoHistoricoPage"))),
  [PermissaoEnum.APROVACAO_CONFIG_MANAGE],
);
const DespesaListPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/producao/despesas/DespesaListPage"))),
  [PermissaoEnum.DESPESA_PROJETO_FIND_ALL],
);
const DespesaDetailPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/producao/despesas/DespesaDetailPage"))),
  [PermissaoEnum.DESPESA_PROJETO_FIND_ALL],
);
const DespesaLancarPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/producao/despesas/DespesaLancarPage"))),
  [PermissaoEnum.DESPESA_PROJETO_CREATE],
);
const ComissaoListPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/controladoria/comissoes/ComissaoListPage"))),
  [PermissaoEnum.COMISSAO_FIND_ALL],
);
const ComissaoDetailPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/controladoria/comissoes/ComissaoDetailPage"))),
  [PermissaoEnum.COMISSAO_FIND_ALL],
);
const ComissaoLancarPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/controladoria/comissoes/ComissaoLancarPage"))),
  [PermissaoEnum.COMISSAO_CREATE],
);
const AprovacaoDespesasPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/producao/aprovacoes/AprovacaoDespesasPage"))),
  [PermissaoEnum.ROTA_APROVACAO_DESPESA_DECIDIR],
);
const AprovacaoOrcamentoPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/custo-fixo/AprovacaoOrcamentoPage"))),
  [PermissaoEnum.ROTA_APROVACAO_BUDGET_DECIDIR],
);
const ModeracaoOrcamentoPage = withPermissionPage(
  Loadable(lazy(() => import("./pages/custo-fixo/ModeracaoOrcamentoPage"))),
  [PermissaoEnum.ROTA_APROVACAO_BUDGET_DECIDIR],
);
const PessoaPerfilPage = Loadable(lazy(() => import("./pages/pessoa/PessoaPerfilPage")));
const PerfilNotificacoesPage = Loadable(
  lazy(() => import("./pages/pessoa/PerfilNotificacoesPage")),
);
const PerfilFeriasPage = Loadable(lazy(() => import("./pages/pessoa/PerfilFeriasPage")));

const DashboardMenuPage = Loadable(lazy(() => import("./pages/dashboard/DashboardMenuPage")));
const DashboardByNamePage = Loadable(lazy(() => import("./pages/dashboard/DashboardByNamePage")));

const SupersetDashboardPage = Loadable(
  lazy(() => import("./pages/dashboard/SupersetDashboardPage")),
);

const TarefaPage = Loadable(lazy(() => import("./pages/tarefas/TarefaListPage")));
const TarefaPublicasListPage = Loadable(
  lazy(() => import("./pages/tarefas/TarefaPublicasListPage")),
);
const TarefaCreatePage = Loadable(lazy(() => import("./pages/tarefas/TarefaCreatePage")));

// --- Root Wrapper para Contextos de Navegação ---
const RootContexts = () => (
  <NavigationProvider>
    <AprovacaoNotificationsProvider>
      <Outlet />
    </AprovacaoNotificationsProvider>
  </NavigationProvider>
);

// --- Configuração das Rotas ---
// --- Configuração das Rotas Agrupadas ---
export const router = createBrowserRouter([
  {
    element: <RootContexts />, // Envolve toda a aplicação com os contextos de navegação globais
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: "/login",
        element: <Login />,
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/",
            element: <MainLayout />,
            children: [
              { index: true, element: <Menu /> },
              { path: "modulo/:parentId", element: <SubMenuDashboard /> },

              // 💼 Módulo: Jobs & Planejamento
              {
                path: "job",
                children: [
                  { index: true, element: <JobPage /> },
                  { path: "novo", element: <JobPageCreate /> },
                  { path: ":id", element: <JobPageDetail /> },
                  { path: ":id/editar", element: <JobPageEdit /> },
                ],
              },

              {
                path: "jobs",
                children: [
                  { path: "planejamento", element: <JobPlanejamentoListPage /> },
                  { path: "planejamento/agenda", element: <JobPlanejamentoAgendaPage /> },
                  { path: ":id/planejamento", element: <JobPlanejamentoPageDetail /> },
                  { path: "tecnologia", element: <JobTecnologiaListPage /> },
                  { path: "tecnologia/agenda", element: <JobTecnologiaAgendaPage /> },
                  { path: ":id/tecnologia", element: <JobTecnologiaPageDetail /> },
                  { path: "criacao", element: <CriacaoListPage /> },
                  { path: "criacao/:id", element: <CriacaoJobPageDetail /> },
                ],
              },

              // 📊 Módulo: Planilhas (Atendimento, Pré-produção, Compras, Produção Gráfica)
              {
                path: "planilhas",
                children: [
                  // Atendimento
                  { path: "atendimento", element: <AtendimentoList /> },
                  { path: "atendimento/clientes", element: <AtendimentoClientesPage /> },
                  { path: "atendimento/agenda", element: <AgendaJobsPage /> },
                  { path: "atendimento/novo", element: <AtendimentoCreate /> },
                  { path: ":id/atendimento", element: <AtendimentoPageDetail /> },
                  { path: ":id/atendimento/editar", element: <AtendimentoEdit /> },

                  // Pré-produção
                  { path: "pre-producao", element: <PreProductionList /> },
                  { path: "pre-producao/agenda", element: <PreProducaoAgendaPage /> },
                  { path: ":id/pre-producao", element: <PreProducaoPageDetails /> },
                  { path: ":id/pre-producao/versao/nova", element: <PreProducaoVersaoCreate /> },

                  // Compras
                  { path: "compras", element: <CompraList /> },
                  { path: ":id/compras", element: <CompraPageDetail /> },
                  { path: ":id/compras/novo", element: <CompraPageCreate /> },
                  { path: ":id/compras/editar", element: <CompraPageEdit /> },

                  // Produção Gráfica
                  { path: "producao-grafica", element: <PlanilhaProducaoGraficaListPage /> },
                  {
                    path: ":planilhaId/producao-grafica",
                    element: <PlanilhaProducaoGraficaDetailPage />,
                  },
                ],
              },

              // 📈 Módulo: Produtividade por Área (docs/produtividade-por-area.md)
              // Fase 1: só Atendimento — as demais áreas entram junto da sua fase (§12.3).
              {
                path: "produtividade",
                children: [{ path: "atendimento", element: <AtendimentoProdutividadePage /> }],
              },

              // 💰 Módulo: Faturamento & Creditáveis
              {
                path: "faturamento",
                children: [
                  { path: "atendimento", element: <FaturamentoAtendimentoList /> },
                  { path: "atendimento/novo", element: <FaturamentoAtendimentoCreate /> },
                  { path: "atendimento/:id", element: <FaturamentoAtendimentoDetails /> },
                ],
              },
              {
                path: "creditaveis",
                children: [
                  { index: true, element: <CreditaveisList /> },
                  { path: "grupo-credito/:id", element: <CreditoDetails /> },
                ],
              },

              // 📈 Módulo: Financeiro & Produção Financeira
              {
                path: "producao-financeiro",
                children: [
                  {
                    path: "aps",
                    children: [
                      { index: true, element: <ProducaoFinanceiraList /> },
                      { path: "novo", element: <ProducaoFinanceiraCreate /> },
                      { path: ":orcamento", element: <ProducaoFinanceiraDetails /> },
                      { path: ":orcamento/editar", element: <ProducaoFinanceiraEdit /> },
                    ],
                  },
                  {
                    path: "conciliacoes",
                    children: [
                      { index: true, element: <ConciliacaoList /> },
                      { path: ":id", element: <ConciliacaoDetails /> },
                      { path: ":id/conciliar", element: <ConciliacaoConciliar /> },
                    ],
                  },
                ],
              },
              {
                path: "financeiro",
                children: [
                  { path: "faturamentos", element: <FaturamentoFinanceiroList /> },
                  { path: "faturamentos/:id", element: <FaturamentoFinanceiroDetails /> },
                  { path: "gestao", element: <CustoFixoPage /> },
                ],
              },
              { path: "atendimento/contatos", element: <ContatosPage /> },
              { path: "atendimento/contatos/novo", element: <ContatoCreatePage /> },
              { path: "atendimento/contatos/pedir-brinde", element: <PedirBrindePage /> },
              {
                path: "atendimento/contatos/pedidos-brinde/:id",
                element: <PedidoBrindeDetalhePage />,
              },
              { path: "atendimento/contatos/:id", element: <ContatoDetalhePage /> },
              { path: "pre-producao/contatos", element: <ContatosPage /> },
              { path: "pre-producao/contatos/novo", element: <ContatoCreatePage /> },
              { path: "pre-producao/contatos/pedir-brinde", element: <PedirBrindePage /> },
              {
                path: "pre-producao/contatos/pedidos-brinde/:id",
                element: <PedidoBrindeDetalhePage />,
              },
              { path: "pre-producao/contatos/:id", element: <ContatoDetalhePage /> },
              { path: "compras/contatos", element: <ContatosPage /> },
              { path: "compras/contatos/novo", element: <ContatoCreatePage /> },
              { path: "compras/contatos/pedir-brinde", element: <PedirBrindePage /> },
              { path: "compras/contatos/pedidos-brinde/:id", element: <PedidoBrindeDetalhePage /> },
              { path: "compras/contatos/:id", element: <ContatoDetalhePage /> },
              // A Facilities não tem lista de contatos: a fila dela é a de gifts. As rotas de baixo seguem (ficha e pedido).
              {
                path: "facilities/contatos",
                element: <Navigate to="/facilities/envios" replace />,
              },
              { path: "facilities/contatos/novo", element: <ContatoCreatePage /> },
              { path: "facilities/contatos/pedir-brinde", element: <PedirBrindePage /> },
              {
                path: "facilities/contatos/pedidos-brinde/:id",
                element: <PedidoBrindeDetalhePage />,
              },
              { path: "facilities/contatos/:id", element: <ContatoDetalhePage /> },
              { path: "facilities/envios", element: <EnviosPage /> },
              { path: "custo-fixo", element: <CustoFixoContratosPage /> },
              { path: "custo-fixo/cadastros", element: <CustoFixoCadastrosPage /> },
              // 'novo' antes de ':id' — senão a rota de detalhe engole a de criação.
              { path: "custo-fixo/contratos/novo", element: <ContratoCreatePage /> },
              { path: "custo-fixo/contratos/:id", element: <ContratoDetalhePage /> },
              { path: "custo-fixo/centros/:id", element: <CentroCustoDetalhePage /> },
              { path: "custo-fixo/planejamento", element: <PlanejamentoPage /> },
              // 'nova-versao' antes de qualquer rota por id — mesmo cuidado do 'novo' de contratos.
              {
                path: "custo-fixo/planejamento/nova-versao",
                element: <PlanejamentoNovaVersaoPage />,
              },
              {
                path: "custo-fixo/planejamento/versao/:versaoId/editar",
                element: <PlanejamentoEditarVersaoPage />,
              },
              { path: "custo-fixo/consolidacao", element: <ConsolidacaoPage /> },
              { path: "custo-fixo/fechamento", element: <FechamentoHubPage /> },
              // Antes da rota por centro: "consolidado" e "painel" são caminho fixo, não um id de área.
              { path: "custo-fixo/fechamento/consolidado", element: <FechamentoConsolidadoPage /> },
              { path: "custo-fixo/fechamento/:centroId", element: <FechamentoPage /> },

              // ✅ Módulo: Aprovações & Diversos
              {
                path: "pre-producao/versoes",
                element: <VersoesListPage />, // Rota solta de pré-produção
              },
              // ✅ Módulo Aprovações — fila de orçamentos + filas segmentadas por processo
              {
                path: "aprovacoes",
                children: [
                  { index: true, element: <AprovacaoPage /> },
                  { path: "despesas", element: <AprovacaoDespesasPage /> },
                  { path: "orcamento", element: <AprovacaoOrcamentoPage /> },
                  { path: "orcamento/:fluxoId", element: <ModeracaoOrcamentoPage /> },
                ],
              },

              // ✅ Módulo Produção — despesas extraordinárias (§7), com abrangência de equipe
              {
                path: "producao",
                children: [
                  { path: "despesas-extraordinarias", element: <DespesaListPage /> },
                  { path: "despesas-extraordinarias/novo", element: <DespesaLancarPage /> },
                  { path: "despesas-extraordinarias/:id", element: <DespesaDetailPage /> },
                ],
              },

              // ✅ Módulo Controladoria — comissões (§7), sem vínculo nesta fase
              {
                path: "controladoria",
                children: [
                  { path: "comissoes", element: <ComissaoListPage /> },
                  { path: "comissoes/novo", element: <ComissaoLancarPage /> },
                  { path: "comissoes/:id", element: <ComissaoDetailPage /> },
                ],
              },

              // ✅ Módulo: Tarefas
              {
                path: "tarefas",
                children: [
                  { index: true, element: <TarefaPage /> },
                  { path: "publicas", element: <TarefaPublicasListPage /> },
                  { path: "criar", element: <TarefaCreatePage /> },
                  { path: ":id", element: <TarefaDetailPage /> },
                ],
              },

              // 📣 Histórico de comunicados (qualquer usuário logado)
              { path: "comunicados", element: <ComunicadosHistoricoPage /> },

              // 👤 Módulo: Perfil (cada seção é uma página dedicada)
              { path: "perfil", element: <PessoaPerfilPage /> },
              { path: "perfil/notificacoes", element: <PerfilNotificacoesPage /> },
              { path: "perfil/ferias", element: <PerfilFeriasPage /> },

              // 🛠️ Módulo: Admin & Dashboards
              {
                path: "admin",
                children: [
                  { path: "usuarios", element: <UserListaPage /> },
                  { path: "usuarios/novo", element: <UsuarioCreate /> },
                  { path: "cargos", element: <RolePermissionsPage /> },
                  { path: "tarefas", element: <TarefaAdminListPage /> },
                  { path: "clientes", element: <ClienteAdminListPage /> },
                  { path: "clientes/:id/painel", element: <PainelClientePage /> },
                  { path: "squads", element: <SquadAdminListPage /> },
                  { path: "processamento-ia", element: <ProcessamentoIAAdminPage /> },
                  { path: "arquivos", element: <ArquivosAdminPage /> },
                  { path: "notificacoes", element: <NotificacoesAdminPage /> },
                  { path: "comunicados", element: <ComunicadosAdminPage /> },
                  { path: "comunicados/novo", element: <ComunicadoSistemaCreatePage /> },
                  { path: "comunicados/:id", element: <ComunicadoDetailsPage /> },
                  { path: "comunicados/:id/editar", element: <ComunicadoSistemaEditPage /> },
                  {
                    path: "servicos-externos",
                    children: [
                      { index: true, element: <ServicosExternosPage /> },
                      { path: "notificacao-teste", element: <NotificacaoTestePage /> },
                    ],
                  },
                  { path: "aprovacoes", element: <AprovacaoConfigListPage /> },
                  { path: "aprovacoes/editor", element: <AprovacaoConfigEditorPage /> },
                  { path: "aprovacoes/historico", element: <FluxoAprovacaoHistoricoPage /> },
                ],
              },
              {
                path: "dashboard",
                children: [
                  { index: true, element: <DashboardMenuPage /> },
                  { path: ":dashboardName", element: <DashboardByNamePage /> },
                  { path: "superset/:dashboardName", element: <SupersetDashboardPage /> },
                ],
              },

              // 🚫 Catch All (404 Interno)
              { path: "*", element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
]);
